import React, { useState, useEffect, useRef } from 'react';
import { sendCreateOutboundFlagTrip } from '../Itinerary/itinerarySocketHelper';

/**
 * FlagTripDropoffModal - Modal for recording passengers exiting at outbound stops
 * Used in demand response mode when passengers board at the hub and exit elsewhere
 */
const FlagTripDropoffModal = ({
  isOpen,
  onClose,
  maxPassengers,
  currentLocation,
  baseLat,
  baseLon,
  lastHubDepartureTime,
  routeId,
  vehicleId,
  driverId,
  onSuccess
}) => {
  const [passengerCount, setPassengerCount] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Deduplication: track last submit timestamp to prevent rapid double-taps
  const lastSubmitRef = useRef(0);
  const SUBMIT_COOLDOWN_MS = 5000; // 5 second cooldown between submissions

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setPassengerCount(1);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Ensure passenger count doesn't exceed max (but always allow at least 1)
  useEffect(() => {
    if (maxPassengers > 0 && passengerCount > maxPassengers) {
      setPassengerCount(maxPassengers);
    }
  }, [maxPassengers, passengerCount]);

  const handleSubmit = async () => {
    // Validation
    if (!passengerCount || passengerCount < 1) {
      setError('Please enter at least 1 passenger');
      return;
    }

    // Only check max if there are passengers on vehicle
    if (maxPassengers > 0 && passengerCount > maxPassengers) {
      setError(`Cannot drop more than ${maxPassengers} passenger(s)`);
      return;
    }

    if (!currentLocation || !currentLocation.lat || !currentLocation.lng) {
      setError('GPS location not available. Please wait for GPS signal.');
      return;
    }

    if (!baseLat || !baseLon) {
      setError('Hub location not available');
      return;
    }

    if (!routeId || !vehicleId) {
      console.error('FlagTripDropoffModal: Missing routeId or vehicleId', { routeId, vehicleId });
      setError('Vehicle or route data not available. Please go back and try again.');
      return;
    }

    if (!driverId) {
      console.warn('FlagTripDropoffModal: driverId not available client-side, server will resolve from vehicle');
    }

    // Deduplication: reject rapid re-submits within cooldown window
    const now = Date.now();
    if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
      console.warn('FlagTripDropoffModal: Submit rejected - too soon after last submit (' + (now - lastSubmitRef.current) + 'ms)');
      setError('Please wait a moment before recording another exit.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    lastSubmitRef.current = now;

    try {
      // Get fresh GPS position at submit time to avoid stale coordinates
      // Falls back to the currentLocation prop if fresh read fails or times out
      let destLat = currentLocation.lat;
      let destLng = currentLocation.lng;

      try {
        const freshPosition = await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => reject(new Error('GPS timeout')), 2000);
          navigator.geolocation.getCurrentPosition(
            (position) => {
              clearTimeout(timeoutId);
              resolve(position);
            },
            (gpsErr) => {
              clearTimeout(timeoutId);
              reject(gpsErr);
            },
            { enableHighAccuracy: true, timeout: 2000, maximumAge: 0 }
          );
        });
        destLat = freshPosition.coords.latitude;
        destLng = freshPosition.coords.longitude;
        console.log('FlagTripDropoffModal: Using fresh GPS position:', { destLat, destLng });
      } catch (gpsErr) {
        // Fall back to prop-based location (may be slightly stale but still valid)
        console.warn('FlagTripDropoffModal: Fresh GPS unavailable, using last known position:', gpsErr.message);
      }

      // Create the outbound flag trip for passengers exiting
      const tripData = {
        routeId: routeId,
        vehicleId: vehicleId,
        driverId: driverId,
        passengerCount: parseInt(passengerCount, 10),
        // Origin = hub (where passenger boarded) - same for all trips in a block by design
        originLat: baseLat,
        originLon: baseLon,
        originTime: lastHubDepartureTime,
        // Destination = fresh GPS at submit time (where passenger exiting)
        destinationLat: destLat,
        destinationLon: destLng,
        destinationTime: new Date().toISOString()
      };

      console.log('Creating outbound flag trip for passenger dropoff:', tripData);
      sendCreateOutboundFlagTrip(tripData);

      // Delay onSuccess callback to prevent rapid re-opens of the modal
      // The 3-second delay keeps isSubmitting=true, blocking the UI
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(parseInt(passengerCount, 10));
        }
      }, 3000);
    } catch (err) {
      console.error('Error creating outbound flag trip:', err);
      setError('Failed to create trip. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handlePassengerChange = (e) => {
    const value = parseInt(e.target.value, 10);
    // Allow at least 1, check max only if maxPassengers > 0
    const maxLimit = maxPassengers > 0 ? maxPassengers : 999;
    if (!isNaN(value) && value >= 1 && value <= maxLimit) {
      setPassengerCount(value);
    } else if (e.target.value === '') {
      setPassengerCount('');
    }
  };

  const incrementPassengers = () => {
    // Allow increment up to max, or no limit if maxPassengers is 0
    const maxLimit = maxPassengers > 0 ? maxPassengers : 999;
    setPassengerCount(prev => Math.min(maxLimit, (parseInt(prev, 10) || 0) + 1));
  };

  const decrementPassengers = () => {
    setPassengerCount(prev => Math.max(1, (parseInt(prev, 10) || 1) - 1));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden m-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-orange-500">
          <h2 className="text-xl font-semibold text-white">Passengers Exiting</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Current passenger count indicator */}
          <div className="mb-4 text-center">
            <p className="text-gray-600 text-sm">
              Current passengers on vehicle: <span className="font-bold text-lg">{maxPassengers}</span>
            </p>
          </div>

          {/* Passenger Count Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Number of Passengers Exiting
            </label>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={decrementPassengers}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-2xl font-bold text-white transition-colors"
                disabled={isSubmitting || passengerCount <= 1}
              >
                -
              </button>
              <input
                type="number"
                min="1"
                max={maxPassengers > 0 ? maxPassengers : 999}
                value={passengerCount}
                onChange={handlePassengerChange}
                className="w-24 h-16 text-center text-3xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={isSubmitting}
              />
              <button
                onClick={incrementPassengers}
                className="w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-2xl font-bold text-white transition-colors"
                disabled={isSubmitting || (maxPassengers > 0 && passengerCount >= maxPassengers)}
              >
                +
              </button>
            </div>
          </div>

          {/* GPS Status */}
          <div className="mb-4 text-center text-sm text-gray-500">
            {currentLocation ? (
              <span className="text-green-600">
                <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                GPS Ready
              </span>
            ) : (
              <span className="text-orange-500">
                <svg className="w-4 h-4 inline mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Waiting for GPS...
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !passengerCount || passengerCount < 1 || !currentLocation}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Recording...
              </>
            ) : (
              'Confirm Exit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagTripDropoffModal;

